/**
 * @module headingBadgePlugin
 * @description Custom Milkdown plugin for rendering heading attributes as visual badges
 *
 * When a heading has Pandoc attributes like {epub:type=titlepage .titlepage .page-end},
 * this plugin:
 * 1. Hides the inline {…} attribute text using a decoration
 * 2. Appends visual badges after the heading showing the attributes
 *
 * Uses ProseMirror decorations (not a NodeView) for maximum compatibility.
 */

import { Plugin, PluginKey } from "@milkdown/kit/prose/state";
import { Decoration, DecorationSet } from "@milkdown/kit/prose/view";
import { $prose } from "@milkdown/kit/utils";
import { DOCUMENT_TYPE_TO_EPUB_TYPE } from "./epubTypePlugin";
import { i18n } from "../stores/i18nStore";
import { get } from "svelte/store";

// Reverse mapping: epub:type value → docType key (for i18n lookup)
const EPUB_TYPE_TO_DOC_TYPE: Record<string, string> = {};
for (const [docType, epubType] of Object.entries(DOCUMENT_TYPE_TO_EPUB_TYPE)) {
  if (!EPUB_TYPE_TO_DOC_TYPE[epubType]) {
    EPUB_TYPE_TO_DOC_TYPE[epubType] = docType;
  }
}

/**
 * Get the localized label for an epub:type value
 */
function getEpubTypeLabel(epubType: string): string {
  const $i18n = get(i18n);
  const docType = EPUB_TYPE_TO_DOC_TYPE[epubType];
  if (docType) {
    const key = `docType.${docType}`;
    const label = $i18n(key);
    if (label !== key) return label;
  }
  return epubType.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Create a badge DOM element for a widget decoration
 */
function createBadgeWidget(
  epubType: string | null,
  classes: string[]
): HTMLElement {
  const container = document.createElement("span");
  container.classList.add("heading-badges");
  container.contentEditable = "false";

  if (epubType) {
    const badge = document.createElement("span");
    badge.classList.add("heading-badge", "heading-badge-type");
    badge.textContent = `Type: ${getEpubTypeLabel(epubType)}`;
    container.appendChild(badge);
  }

  for (const cls of classes) {
    const badge = document.createElement("span");
    badge.classList.add("heading-badge", "heading-badge-class");
    badge.textContent = `.${cls}`;
    container.appendChild(badge);
  }

  return container;
}

/**
 * Parse attributes from a {…} attribute string
 */
function parseInlineAttrs(attrStr: string): { epubType: string | null; classes: string[] } {
  let epubType: string | null = null;
  const classes: string[] = [];
  const tokenRegex = /\.([^\s.#=]+)|([\w:-]+)=("[^"]*"|'[^']*'|[^\s]+)/g;
  let m;
  while ((m = tokenRegex.exec(attrStr)) !== null) {
    if (m[1]) {
      classes.push(m[1]);
    } else if (m[2] && m[3]) {
      if (m[2] === 'epub:type' || m[2] === 'epub__COLON__type') {
        epubType = m[3].replace(/^["']|["']$/g, '');
      }
    }
  }
  return { epubType, classes };
}

const headingBadgePluginKey = new PluginKey("heading-badge-plugin");

/**
 * Build decorations for all headings with attributes
 */
function buildDecorations(doc: any): DecorationSet {
  const decorations: Decoration[] = [];

  doc.descendants((node: any, pos: number) => {
    if (node.type.name !== "heading") return true;

    // First, try to read attributes from ProseMirror node attrs
    let epubType: string | null = null;
    if (node.attrs.dataAttrs) {
      try {
        const dataAttrs = JSON.parse(node.attrs.dataAttrs) as Record<
          string,
          string
        >;
        epubType = dataAttrs["epub:type"] || null;
      } catch {
        // Ignore
      }
    }

    const nodeClasses: string[] = node.attrs.class
      ? node.attrs.class
          .split(" ")
          .filter((c: string) => c.trim())
      : [];

    // Find the {…} text within the heading's inline content
    const headingStart = pos + 1;
    const headingEnd = pos + node.nodeSize - 1;

    // Track what we parse from inline text (more complete than node attrs)
    let inlineEpubType: string | null = null;
    let inlineClasses: string[] = [];
    let foundInlineAttrs = false;

    // Walk through child text nodes to find the {…} pattern
    node.forEach((child: any, childOffset: number) => {
      if (child.isText) {
        const text = child.text as string;
        // Match {…} at end of text, possibly preceded by space
        const attrMatch = text.match(/\s*\{([^}]+)\}\s*$/);
        if (attrMatch) {
          foundInlineAttrs = true;
          const matchStart = headingStart + childOffset + (text.length - attrMatch[0].length);
          const matchEnd = headingStart + childOffset + text.length;

          // Parse attributes from the inline text
          const parsed = parseInlineAttrs(attrMatch[1]);
          inlineEpubType = parsed.epubType;
          inlineClasses = parsed.classes;

          // Inline decoration to hide the {…} text
          decorations.push(
            Decoration.inline(matchStart, matchEnd, {
              class: "heading-attrs-hidden",
            })
          );
        }
      }
    });

    // Use inline-parsed attrs if available (they're more complete),
    // otherwise fall back to node attrs
    const finalEpubType = inlineEpubType || epubType;
    const finalClasses = inlineClasses.length > 0 ? inlineClasses : nodeClasses;

    if (!finalEpubType && finalClasses.length === 0) return true;

    // Widget decoration at end of heading to show badges
    decorations.push(
      Decoration.widget(headingEnd, () => createBadgeWidget(finalEpubType, finalClasses), {
        side: -1,
        key: `heading-badge-${pos}`,
      })
    );

    return true;
  });

  return DecorationSet.create(doc, decorations);
}

/**
 * ProseMirror plugin that adds heading badge decorations
 */
export const headingBadgePlugin = $prose(() => {
  return new Plugin({
    key: headingBadgePluginKey,
    state: {
      init(_, { doc }) {
        return buildDecorations(doc);
      },
      apply(tr, oldDecorations) {
        // Rebuild decorations when document changes
        if (tr.docChanged) {
          return buildDecorations(tr.doc);
        }
        return oldDecorations.map(tr.mapping, tr.doc);
      },
    },
    props: {
      decorations(state) {
        return headingBadgePluginKey.getState(state) as DecorationSet;
      },
    },
  });
});

export default headingBadgePlugin;
